import { ChainType } from "@0xsquid/squid-types";
import { QueryClient } from "@tanstack/react-query";
import get from "lodash/get";
import {
  chainTypeToNativeTokenAddressMap,
  defaultConfigValues,
} from "../../core/constants";
import { keys } from "../../core/queries/queries-keys";
import type {
  AppConfig,
  NamedTokenConfig,
  TokenConfig,
} from "../../core/types/config";
import { SwapDirection } from "../../core/types/swap";
import { Chain, Token, TokenWithBalance } from "../../core/types/tokens";
import {
  getSupportedChainIdsForDirection,
  shareSubgraphId,
} from "./assetsService";

const isNamedTokenConfig = (
  t: TokenConfig | NamedTokenConfig
): t is NamedTokenConfig => "networkName" in t;

export const resolveChainIdFromAsset = (
  asset: TokenConfig | NamedTokenConfig,
  chains: Chain[]
): string | undefined => {
  const chainId = isNamedTokenConfig(asset)
    ? chains.find(
        (c) => c.networkName.toLowerCase() === asset.networkName.toLowerCase()
      )?.chainId
    : asset.chainId;
  return chains.some((c) => c.chainId === chainId) ? chainId : undefined;
};

export const getConfigWithDefaults = (
  config: AppConfig | undefined
): AppConfig => {
  return {
    integratorId: get(config, "integratorId", defaultConfigValues.integratorId),
    slippage: get(config, "slippage", defaultConfigValues.slippage),
    collectFees: get(config, "collectFees", defaultConfigValues.collectFees),
    apiUrl: get(config, "apiUrl", defaultConfigValues.apiUrl),
    priceImpactWarnings: get(
      config,
      "priceImpactWarnings",
      defaultConfigValues.priceImpactWarnings
    ),
    initialAssets: get(
      config,
      "initialAssets",
      defaultConfigValues.initialAssets
    ),
    defaultTokensPerChain: get(
      config,
      "defaultTokensPerChain",
      defaultConfigValues.defaultTokensPerChain
    ),
    loadPreviousStateFromLocalStorage: get(
      config,
      "loadPreviousStateFromLocalStorage",
      defaultConfigValues.loadPreviousStateFromLocalStorage
    ),
    availableChains: get(
      config,
      "availableChains",
      defaultConfigValues.availableChains
    ),
    availableTokens: get(
      config,
      "availableTokens",
      defaultConfigValues.availableTokens
    ),
    disabledChains: get(
      config,
      "disabledChains",
      defaultConfigValues.disabledChains
    ),
    degenMode: get(config, "degenMode", defaultConfigValues.degenMode),
    preHook: get(config, "preHook", defaultConfigValues.preHook),
    postHook: get(config, "postHook", defaultConfigValues.postHook),
    overrideGasRefundAddress: get(
      config,
      "overrideGasRefundAddress",
      defaultConfigValues.overrideGasRefundAddress
    ),
  };
};

export const randomIntFromInterval = (min: number, max: number) => {
  return Math.floor(Math.random() * (max - min + 1) + min);
};

export const getTokensForChain = (
  tokens: Token[],
  chainId: number | string | undefined
) => {
  return chainId ? tokens.filter((t) => t.chainId === chainId) : tokens;
};

/**
 * Returns the first available chain ID for a specific direction based on config.
 * If no initial chains are found in config, returns the first available chain ID.
 * @returns The first available chain ID.
 */
export const getFirstAvailableChainId = (
  config: Pick<
    AppConfig,
    "disabledChains" | "initialAssets" | "availableChains"
  >,
  direction: SwapDirection,
  chains: Chain[] | undefined = []
): string | undefined => {
  const supportedChainIds = getSupportedChainIdsForDirection(
    chains.map((c) => c.chainId),
    config,
    direction
  );

  const availableChains = chains.filter((c) =>
    supportedChainIds.includes(c.chainId)
  );

  // get initial chain ID from config
  const initialChainId = getInitialChainIdFromConfig({
    chains: availableChains,
    config,
    direction,
  });

  if (initialChainId) return initialChainId;

  // if no initial chain ID is found in config, return the first available chain ID
  return availableChains.length > 0 ? availableChains[0].chainId : undefined;
};

export const fetchHighestBalanceToken = (
  fetchCachedBalance?: {
    address?: string;
    queryClient?: QueryClient;
  },
  chainId?: string
): string | undefined => {
  let address = undefined;
  if (fetchCachedBalance) {
    const queryCache = fetchCachedBalance?.queryClient?.getQueryCache();
    // get cached balance from key
    const cache = queryCache?.find(
      keys().allTokensBalance(
        fetchCachedBalance?.address,
        !isNaN(Number(chainId)) ? ChainType.EVM : ChainType.COSMOS
      )
    );

    // Parse result to TokenWithBalance[]
    const cacheResult =
      (cache?.state.data as TokenWithBalance[] | undefined) ?? [];

    if (cacheResult.length > 0) {
      const tokensWithBalance = cacheResult.filter(
        (t) => t.chainId === chainId && Number(t.balance) > 0
      );

      if (tokensWithBalance.length > 0) {
        // Return the token with the highest balance
        // TODO: At the moment we're not taking into account usd price
        const tokenWithHighestBalance = tokensWithBalance.reduce(
          (prev, current) =>
            Number(prev.balance) > Number(current.balance) ? prev : current
        );

        if (tokenWithHighestBalance) {
          address = tokenWithHighestBalance.address;
        }
      }
    }
  }
  return address;
};

const findNativeTokenForChainType = (tokens: Token[]) => {
  return tokens.find(
    (t) =>
      t.address.toLowerCase() ===
      chainTypeToNativeTokenAddressMap[t.type].toLowerCase()
  );
};

/**
 * Returns the default token address for a specific chain and direction based on config.
 * If no default tokens are set in config, returns the native token for the specified chain
 *
 * @param tokens The list of tokens.
 * @param config App config
 * @param chainId The chain ID of the token
 * @param direction The direction of the swap
 * @param unavailableTokenAddress The token that is unavailable. Can be useful when we don't want to show the same token in the opposite direction
 * @returns
 */
export const getInitialOrDefaultTokenAddressForChain = ({
  tokens,
  config,
  chainId,
  direction,
  excludeToken,
  fetchCachedBalance,
}: {
  tokens: Token[];
  config: Pick<
    AppConfig,
    | "initialAssets"
    | "defaultTokensPerChain"
    | "disabledChains"
    | "availableChains"
  >;
  chainId: string | undefined;
  direction: SwapDirection;
  excludeToken?: {
    address?: string;
    chainId?: string;
  };
  fetchCachedBalance?: {
    address?: string;
    queryClient?: QueryClient;
  };
}): string | undefined => {
  if (!chainId) return;

  const supportedChainIds = getSupportedChainIdsForDirection(
    [chainId],
    config,
    direction
  );

  if (!supportedChainIds.includes(chainId)) return;

  const configDefaultTokenForChain = config.defaultTokensPerChain?.find(
    (token) => token.chainId == chainId
  );

  // get default token from config
  if (
    configDefaultTokenForChain &&
    configDefaultTokenForChain.address !== excludeToken?.address &&
    configDefaultTokenForChain.chainId !== excludeToken?.chainId
  ) {
    return configDefaultTokenForChain.address;
  }

  // if no default token is found in config, try to get initial token
  const initialToken = getInitialTokenAddressForChain({
    chainId,
    config,
    direction,
    tokens,
  });

  if (initialToken) return initialToken;

  // At this point all has been tried to return via config params
  // But we can be smarter and see if a token has a balance and select that one
  const highestBalance = fetchHighestBalanceToken(fetchCachedBalance, chainId);
  if (highestBalance) return highestBalance;

  const filteredTokens = chainId
    ? tokens.filter(
        (t) =>
          t.chainId === chainId &&
          !(
            t.address == excludeToken?.address &&
            t.chainId === excludeToken?.chainId
          )
      )
    : tokens;

  return (
    findNativeTokenForChainType(filteredTokens)?.address ||
    filteredTokens[0]?.address
  );
};

/**
 * Retrieves the initial token address for a specific chain and direction.
 * If no initial assets are set in config, returns undefined.
 *
 * @param tokens - The list of tokens.
 * @param config - The application configuration.
 * @param chainId - The ID of the chain.
 * @param direction - The direction ("from" or "to").
 * @returns The initial token address if found; otherwise, the default token address.
 */
export const getInitialTokenAddressForChain = ({
  chainId,
  config,
  direction,
  tokens,
  unavailableTokenAddress,
}: {
  tokens: Token[];
  config: Pick<AppConfig, "initialAssets">;
  chainId: string | undefined;
  direction: SwapDirection;
  unavailableTokenAddress?: string;
}): string | undefined => {
  const initialAsset = config.initialAssets?.[direction];
  if (!initialAsset) return undefined;

  if (isNamedTokenConfig(initialAsset)) {
    if (!initialAsset.symbol) return undefined;
    return tokens.find(
      (t) =>
        t.chainId === chainId &&
        t.symbol.toLowerCase() === initialAsset.symbol!.toLowerCase()
    )?.address;
  }

  if (
    initialAsset.chainId === chainId &&
    initialAsset.address !== unavailableTokenAddress &&
    tokens.some(
      (t) =>
        t.chainId === initialAsset.chainId &&
        t.address.toLowerCase() === initialAsset.address.toLowerCase()
    )
  ) {
    return initialAsset.address;
  }
};

/**
 * Filter tokens for destination chain
 *
 * Case 1: fromToken.bridgeOnly = true
 * Destination token list shows only tokens with the same commonKey as fromToken
 *
 * Case 2: fromToken.bridgeOnly = false
 * Destination token list shows all tokens with bridgeOnly = false
 * OR Destination token list shows all tokens with the same commonKey as fromToken
 * @param tokens
 * @param selectedDestinationChain
 * @param selectedSourceToken
 * @param removeSourceToken
 * @returns
 */
export const filterTokensForDestination = <T extends Token>({
  tokens,
  selectedDestinationChain,
  selectedSourceToken,
  removeSourceToken = false,
}: {
  tokens: T[];
  selectedDestinationChain: Chain | undefined;
  selectedSourceToken: Token | undefined;
  removeSourceToken?: boolean;
}): T[] => {
  let filteredTokens = [];

  if (selectedSourceToken?.subGraphOnly) {
    filteredTokens = tokens.filter((t) =>
      shareSubgraphId(t, selectedSourceToken)
    );
  }

  filteredTokens = tokens.filter(
    (t) => !t.subGraphOnly || shareSubgraphId(t, selectedSourceToken as Token)
  );

  return filteredTokens.filter((t) => {
    const areSameToken =
      t.address === selectedSourceToken?.address &&
      t.chainId === selectedSourceToken.chainId;

    const isChainValid = !!selectedDestinationChain
      ? t.chainId === selectedDestinationChain?.chainId
      : true;

    if (removeSourceToken) return !areSameToken && isChainValid;
    // return all tokens if destination chain is not selected
    return isChainValid;
  });
};

/**
 * Returns the initial chain ID for a specific direction based on the provided chains and config.
 * @returns The initial chain ID if found in config; otherwise, undefined.
 */
export const getInitialChainIdFromConfig = ({
  config,
  direction,
  chains = [],
}: {
  config: Pick<
    AppConfig,
    "initialAssets" | "disabledChains" | "availableChains"
  >;
  direction: SwapDirection;
  chains: Chain[] | undefined;
}): string | undefined => {
  if (chains.length === 0) return undefined;

  const supportedChainIds = getSupportedChainIdsForDirection(
    chains.map((c) => c.chainId),
    config,
    direction
  );

  const availableChains = chains.filter((c) =>
    supportedChainIds.includes(c.chainId)
  );

  const initialAsset = config.initialAssets?.[direction];
  if (!initialAsset) return undefined;

  if (isNamedTokenConfig(initialAsset)) {
    return availableChains.find(
      (c) =>
        c.networkName.toLowerCase() === initialAsset.networkName.toLowerCase()
    )?.chainId;
  }

  if (availableChains.some((c) => c.chainId === initialAsset.chainId)) {
    return initialAsset.chainId;
  }

  return undefined;
};
